export const MAX_TRIAL_DAYS = 3650;
export const DEFAULT_TRIAL_DAYS = 14;

/** Keep in sync with TRIAL_PLAN_KEYS in _shared/staffOnboarding.ts. */
export const TRIAL_PLAN_KEYS = ["builder", "starter", "growing", "premium", "enterprise"];

export interface TrialAccount {
  business_id: string;
  business_name: string | null;
  email: string | null;
  subscription_status: string | null;
  subscription_tier: string | null;
  next_billing_date: string | null;
  has_bookings_addon: boolean;
  onboarding_complete: boolean;
}

export function validTrialDays(days: number): boolean {
  return Number.isInteger(days) && days >= 1 && days <= MAX_TRIAL_DAYS;
}

export function trialEndDate(days: number, now = new Date()): Date {
  return new Date(now.getTime() + days * 86_400_000);
}

export function formatTrialDate(value: Date | string): string {
  return new Date(value).toLocaleDateString("en-SG", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function trialDaysLeft(endsAt: string, now = new Date()): number {
  return Math.max(0, Math.ceil((new Date(endsAt).getTime() - now.getTime()) / 86_400_000));
}

export function trialIsRunning(account: Pick<TrialAccount, "subscription_status">): boolean {
  return account.subscription_status === "trialing";
}

/** Where a trial stands, in the words the Trial accounts page shows. */
export function trialStatusLabel(
  account: Pick<TrialAccount, "subscription_status" | "next_billing_date">,
  now = new Date(),
): string {
  if (!trialIsRunning(account)) {
    return account.subscription_status === "trial_expired" ? "Expired" : "Not on a trial";
  }
  if (!account.next_billing_date) return "Never expires";
  const days = trialDaysLeft(account.next_billing_date, now);
  return days === 0 ? "Ends today" : `${days} ${days === 1 ? "day" : "days"} left`;
}

/** An expired trial is on the Free floor, so start the next one on Premium. */
export function defaultTrialPlan(tier: string | null | undefined): string {
  return tier && TRIAL_PLAN_KEYS.includes(tier) ? tier : "premium";
}

export function trialLengthLabel(days: number, neverExpires: boolean): string {
  if (neverExpires) return "Never expires";
  return `${days} ${days === 1 ? "day" : "days"}`;
}
